import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import {
  getImageEditorFormatMeta,
  normalizeImageEditorFormat,
} from './imageEditorFormats';
import { upsertProjectGalleryAssetClient } from './syncProjectGalleryAsset';
import {
  buildSaveFilePreview,
  sanitizeAssetSuffix,
} from './imageEditorGuides';

/**
 * Resolve a manifest asset id the same way Render Queue does.
 * @param {object} asset
 * @param {string} manifestId
 * @param {number} index
 * @returns {string}
 */
export function resolveManifestAssetId(asset, manifestId, index) {
  if (asset && typeof asset.id === 'string' && asset.id.trim()) {
    return asset.id.trim();
  }
  return `asset_${manifestId || 'row'}_${index + 1}`;
}

/**
 * @param {unknown} raw
 * @param {number} [max]
 * @returns {string}
 */
export function sanitizeAssetIdPart(raw, max = 60) {
  return String(raw || '')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, max);
}

/**
 * Build the new asset id from Save-as meta + source id (collision suffix applied later).
 * @param {{
 *   sourceAssetId: string,
 *   meta?: { suffix?: string, fileStem?: string },
 * }} params
 * @returns {string}
 */
export function buildEditedAssetId(params) {
  const safeSourceId = sanitizeAssetIdPart(params.sourceAssetId, 60) || 'asset';
  const meta = params.meta && typeof params.meta === 'object' ? params.meta : {};
  const rawSuffix = typeof meta.suffix === 'string' ? meta.suffix.trim() : '';
  const cleanSuffix = sanitizeAssetSuffix(rawSuffix);
  let newAssetId =
    (typeof meta.fileStem === 'string' && meta.fileStem.trim()) ||
    (cleanSuffix
      ? `${safeSourceId}_${cleanSuffix}`
      : `${safeSourceId}_edit_${Date.now()}`);
  return sanitizeAssetIdPart(newAssetId, 120) || `${safeSourceId}_edit_${Date.now()}`;
}

/**
 * Persist an edited image into a game project.
 *
 * - When `sourceAsset.manifestId` is present: append a NEW manifest asset variant
 *   (never mutates the source asset URLs) and upsert the linked gallery card.
 * - When no manifestId (local upload / orphan gallery source) AND projectId set:
 *   upload Storage and create a NEW orphan gallery doc (project-scoped).
 *
 * Saves with no project go through `saveImageEditorDraft` instead (Drafts).
 *
 * @param {{
 *   companyId: string,
 *   projectId: string,
 *   blob: Blob,
 *   meta?: { suffix?: string, fileStem?: string, format?: string },
 *   sourceAsset?: {
 *     id?: string,
 *     manifestId?: string|null,
 *     type?: string,
 *     category?: string,
 *     prompt?: string,
 *     assetName?: string,
 *   }|null,
 *   uid?: string|null,
 * }} params
 * @returns {Promise<{
 *   mode: 'manifest_variant'|'orphan_gallery',
 *   assetId: string,
 *   downloadUrl: string,
 *   storagePath: string,
 *   galleryDocId: string,
 *   format: string,
 * }>}
 */
export async function saveEditedAssetVariant(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const projectId =
    typeof params.projectId === 'string' ? params.projectId.trim() : '';
  const blob = params.blob;
  const uid = params.uid || null;
  const source =
    params.sourceAsset && typeof params.sourceAsset === 'object'
      ? params.sourceAsset
      : {};
  const manifestId =
    typeof source.manifestId === 'string' ? source.manifestId.trim() : '';
  const sourceAssetId =
    typeof source.id === 'string' && source.id.trim()
      ? source.id.trim()
      : typeof source.assetName === 'string' && source.assetName.trim()
        ? source.assetName.trim()
        : 'local';

  if (!companyId || !projectId || !blob) {
    throw new Error('companyId, projectId, and blob are required.');
  }

  const format = normalizeImageEditorFormat(params.meta?.format);
  const formatMeta = getImageEditorFormatMeta(format);

  let newAssetId = buildEditedAssetId({
    sourceAssetId,
    meta: params.meta,
  });

  const storagePath =
    `companies/${companyId}/games/${projectId}/assets/` +
    `edited_${newAssetId}_${Date.now()}.${formatMeta.extension}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, blob, { contentType: formatMeta.mimeType });
  const downloadUrl = await getDownloadURL(storageRef);

  if (manifestId) {
    const manifestRef = doc(
      db,
      'companies',
      companyId,
      'game_manifests',
      manifestId,
    );
    const snap = await getDoc(manifestRef);
    if (!snap.exists()) {
      throw new Error('Manifest not found.');
    }

    const data = snap.data() || {};
    const current = Array.isArray(data.assets) ? data.assets : [];
    const sourceAsset = current.find(
      (asset, index) =>
        resolveManifestAssetId(asset, manifestId, index) === sourceAssetId,
    );
    if (!sourceAsset) {
      throw new Error('Source asset not found in manifest.');
    }

    const idTaken = current.some(
      (asset, index) =>
        resolveManifestAssetId(asset, manifestId, index) === newAssetId,
    );
    if (idTaken) {
      newAssetId = `${newAssetId}_${Date.now()}`;
    }

    /** @type {Record<string, unknown>} */
    const newAsset = {
      id: newAssetId,
      type:
        typeof sourceAsset.type === 'string' && sourceAsset.type.trim()
          ? sourceAsset.type.trim()
          : typeof source.type === 'string' && source.type.trim()
            ? source.type.trim()
            : 'sprite',
      category:
        typeof sourceAsset.category === 'string' && sourceAsset.category.trim()
          ? sourceAsset.category.trim()
          : typeof source.category === 'string' && source.category.trim()
            ? source.category.trim()
            : 'prop',
      prompt:
        typeof sourceAsset.prompt === 'string'
          ? sourceAsset.prompt
          : typeof source.prompt === 'string'
            ? source.prompt
            : '',
      status: 'needs_review',
      previewUrl: downloadUrl,
      imageUrl: downloadUrl,
      storagePath,
      sourceAssetId,
      errorMessage: null,
    };

    await updateDoc(manifestRef, {
      assets: [...current, newAsset],
      updatedAt: serverTimestamp(),
    });

    let galleryDocId = '';
    try {
      galleryDocId = await upsertProjectGalleryAssetClient({
        companyId,
        projectId,
        manifestId,
        asset: newAsset,
        replaceStorage: false,
        uid,
      });
    } catch (galleryErr) {
      console.warn('saveEditedAssetVariant gallery sync:', galleryErr);
    }

    return {
      mode: 'manifest_variant',
      assetId: newAssetId,
      downloadUrl,
      storagePath,
      galleryDocId: galleryDocId || '',
      format,
    };
  }

  // Orphan gallery path (local upload or gallery card with no manifest link)
  // when a project IS selected. No-project saves use Drafts instead.
  const preview = buildSaveFilePreview(
    (typeof params.meta?.fileStem === 'string' && params.meta.fileStem.trim()) ||
      sourceAssetId,
    format,
  );
  const assetName = preview.fileStem || newAssetId;
  const galleryDocId = `orphan_${sanitizeAssetIdPart(newAssetId, 80)}_${Date.now()}`
    .replace(/_+/g, '_')
    .slice(0, 120);

  /** @type {Record<string, unknown>} */
  const payload = {
    assetName,
    type:
      typeof source.type === 'string' && source.type.trim()
        ? source.type.trim()
        : 'sprite',
    category:
      typeof source.category === 'string' && source.category.trim()
        ? source.category.trim()
        : 'prop',
    imageUrl: downloadUrl,
    storagePath,
    promptUsed:
      typeof source.prompt === 'string' ? source.prompt.trim() : '',
    status: 'needs_review',
    source: 'image_editor_local',
    format,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdByUid: uid,
    updatedByUid: uid,
  };
  if (sourceAssetId && sourceAssetId !== 'local') {
    payload.sourceAssetId = sourceAssetId;
  }

  await setDoc(
    doc(db, 'companies', companyId, 'game_projects', projectId, 'assets', galleryDocId),
    payload,
  );

  return {
    mode: 'orphan_gallery',
    assetId: newAssetId,
    downloadUrl,
    storagePath,
    galleryDocId,
    format,
  };
}
